import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, currencySymbol } from "@/lib/api";
import { LOGO_URL } from "@/components/Logo";
import { Search, ExternalLink, ChevronDown, ListFilter, ShoppingBag, Plus, Minus, X, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription } from "@/components/ui/sheet";
import { DIETARY_TAGS, TAG_BY_ID, TagIcon, normalizeDietary } from "@/lib/dietaryTags";
import { Dish } from "@/components/Dish";
import { useCart } from "@/lib/useCart";

function buildGrouped(categories, subcategories, filteredItems) {
  return categories
    .map((c) => {
      const catItems = filteredItems.filter((i) => i.category_id === c.id);
      const catSubs = subcategories.filter((s) => s.category_id === c.id);
      const subGroups = catSubs
        .map((s) => ({ sub: s, items: catItems.filter((i) => i.subcategory_id === s.id) }))
        .filter((g) => g.items.length > 0);
      const uncategorized = catItems.filter((i) => !i.subcategory_id);
      const totalCount = uncategorized.length + subGroups.reduce((s, g) => s + g.items.length, 0);
      return { cat: c, subGroups, uncategorized, totalCount };
    })
    .filter((g) => g.totalCount > 0);
}

export default function CustomerMenu() {
  const { slug } = useParams();
  const [menu, setMenu] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [diets, setDiets] = useState([]); // multi-select: empty = show all
  const [collapsed, setCollapsed] = useState({}); // { catId: bool }
  const [cartOpen, setCartOpen] = useState(false);
  const [jumpOpen, setJumpOpen] = useState(false);

  useEffect(() => {
    api.get(`/public/menu/${slug}`)
      .then((r) => setMenu(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Menu unavailable"));
  }, [slug]);

  const allItems = menu?.items || [];
  const { lines: cartLines, count: cartCount, total: cartTotal, addItem, decItem, clearCart, cart } = useCart(allItems);

  const toggleCat = useCallback((id) => setCollapsed((c) => ({ ...c, [id]: !c[id] })), []);

  const toggleDiet = useCallback((id) => {
    setDiets((curr) => (curr.includes(id) ? curr.filter((x) => x !== id) : [...curr, id]));
  }, []);

  const filteredItems = useMemo(() => {
    if (!menu) return [];
    return menu.items.filter((i) => {
      if (q && !(`${i.name} ${i.description || ""}`.toLowerCase().includes(q.toLowerCase()))) return false;
      if (diets.length > 0) {
        const tags = normalizeDietary(i.dietary);
        // AND-match: item must carry EVERY selected dietary tag.
        if (!diets.every((d) => tags.includes(d))) return false;
      }
      return true;
    });
  }, [menu, q, diets]);

  // Only show dietary tabs that actually apply to at least one item on this menu.
  const dietaryFilters = useMemo(() => {
    if (!menu) return [];
    const presentTagIds = new Set();
    for (const it of menu.items) {
      for (const t of normalizeDietary(it.dietary)) presentTagIds.add(t);
    }
    return DIETARY_TAGS.filter((t) => presentTagIds.has(t.id)).map((t) => ({ id: t.id, label: t.short }));
  }, [menu]);

  const grouped = useMemo(() => {
    if (!menu) return [];
    return buildGrouped(menu.categories, menu.subcategories, filteredItems);
  }, [menu, filteredItems]);

  const scrollToCat = useCallback((id) => {
    setJumpOpen(false);
    setCollapsed((c) => ({ ...c, [id]: false }));
    setTimeout(() => {
      const el = document.getElementById(`cat-${id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-brand-cream flex flex-col items-center justify-center p-6 text-center" data-testid="menu-error">
        <img src={LOGO_URL} alt="" className="w-24 h-24" />
        <h1 className="font-heading font-bold text-2xl mt-6">Menu unavailable</h1>
        <p className="text-foreground/60 mt-2">{error}</p>
        <Link to="/" className="mt-6 text-brand-orange font-semibold">Go to MenuMojo →</Link>
      </div>
    );
  }
  if (!menu) {
    return <div className="min-h-screen bg-brand-cream flex items-center justify-center text-muted-foreground">Loading menu…</div>;
  }

  const { restaurant } = menu;
  const sym = currencySymbol(restaurant.currency);

  return (
    <div className="min-h-screen bg-brand-cream pb-32" data-testid="customer-menu">
      {/* Hero */}
      <div className="relative">
        <div className="h-48 sm:h-64 bg-brand-navy overflow-hidden relative">
          {restaurant.cover ? (
            <img src={restaurant.cover} alt="" className="w-full h-full object-cover opacity-90" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-brand-navy to-[#1A2B42]" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        </div>
        <div className="max-w-md mx-auto px-5 -mt-12 relative">
          <div className="bg-white rounded-2xl shadow-lg border border-border p-5 flex items-center gap-4">
            <div className="w-16 h-16 rounded-xl border border-border overflow-hidden bg-white shrink-0">
              {restaurant.logo ? (
                <img src={restaurant.logo} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center brand-orange font-heading font-extrabold text-xl">
                  {restaurant.name?.[0] || "M"}
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="font-heading font-bold text-xl break-words" data-testid="restaurant-name-header">{restaurant.name}</h1>
              {restaurant.description && <div className="text-sm text-foreground/60 break-words">{restaurant.description}</div>}
              <div className="text-xs uppercase tracking-[0.15em] font-bold text-brand-orange mt-1">Digital menu</div>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky: Search + Dietary Tabs */}
      <div className="max-w-md mx-auto px-5 mt-6 sticky top-0 z-20 bg-brand-cream/95 backdrop-blur py-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" />
          <Input
            placeholder="Search dishes…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 bg-white border-border rounded-full h-11"
            data-testid="menu-search-input"
          />
        </div>
        {dietaryFilters.length > 0 && (
          <div className="mt-3 -mx-5 px-5 overflow-x-auto scrollbar-hide" data-testid="dietary-tabs">
            <div className="flex gap-2 pb-1 items-center">
              {diets.length > 0 && (
                <button
                  type="button"
                  onClick={() => setDiets([])}
                  data-testid="diet-filter-clear"
                  className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-border text-xs font-bold whitespace-nowrap bg-white text-foreground/60 hover:border-brand-orange"
                >
                  <X className="w-3 h-3" /> Clear
                </button>
              )}
              {dietaryFilters.map((f) => {
                const active = diets.includes(f.id);
                const meta = TAG_BY_ID[f.id];
                return (
                  <button
                    key={f.id}
                    onClick={() => toggleDiet(f.id)}
                    data-testid={`diet-filter-${f.id}`}
                    aria-pressed={active}
                    className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border text-xs font-bold whitespace-nowrap transition ${
                      active
                        ? "bg-brand-navy border-brand-navy text-white"
                        : "bg-white border-border text-foreground/70 hover:border-brand-orange"
                    }`}
                  >
                    {meta && <TagIcon id={f.id} size={12} />}
                    {f.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Menu */}
      <div className="max-w-md mx-auto px-5 mt-4">
        {grouped.length === 0 && (
          <div className="text-center text-muted-foreground py-16" data-testid="menu-empty">
            No dishes match your filter.
          </div>
        )}
        {grouped.map(({ cat, subGroups, uncategorized, totalCount }) => {
          const isCollapsed = !!collapsed[cat.id];
          return (
            <section key={cat.id} id={`cat-${cat.id}`} className="mt-6 scroll-mt-24" data-testid={`category-${cat.id}`}>
              <button
                onClick={() => toggleCat(cat.id)}
                className="w-full flex items-center gap-3 mb-3 text-left"
                data-testid={`toggle-cat-${cat.id}`}
                aria-expanded={!isCollapsed}
              >
                <h2 className="font-heading font-bold text-2xl tracking-tight">{cat.name}</h2>
                <span className="text-xs font-bold brand-orange bg-brand-orange/10 rounded-full px-2 py-0.5">{totalCount}</span>
                <div className="flex-1 border-t border-dashed border-brand-navy/20" />
                <ChevronDown className={`w-5 h-5 text-foreground/50 shrink-0 transition-transform ${isCollapsed ? "-rotate-90" : ""}`} />
              </button>
              {!isCollapsed && (
                <div className="animate-in fade-in duration-200">
                  {uncategorized.length > 0 && (
                    <div className="space-y-3">
                      {uncategorized.map((it) => (
                        <Dish key={it.id} it={it} sym={sym} qty={cart[it.id] || 0} onAdd={addItem} onDec={decItem} />
                      ))}
                    </div>
                  )}
                  {subGroups.map(({ sub, items: si }) => (
                    <div key={sub.id} className="mt-5">
                      <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-orange mb-3">{sub.name}</div>
                      <div className="space-y-3">
                        {si.map((it) => (
                          <Dish key={it.id} it={it} sym={sym} qty={cart[it.id] || 0} onAdd={addItem} onDec={decItem} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </div>

      {/* Jump-to-Category FAB */}
      {grouped.length > 1 && (
        <Sheet open={jumpOpen} onOpenChange={setJumpOpen}>
          <SheetTrigger asChild>
            <button
              className="fixed right-5 bottom-24 z-40 bg-brand-navy text-white w-14 h-14 rounded-full shadow-lg hover:bg-[#1A2B42] flex items-center justify-center"
              aria-label="Jump to category"
              data-testid="jump-fab"
            >
              <ListFilter className="w-5 h-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-2xl max-h-[70vh]" data-testid="jump-sheet">
            <SheetHeader className="text-left">
              <SheetTitle className="font-heading">Jump to</SheetTitle>
              <SheetDescription>Pick a category to scroll to.</SheetDescription>
            </SheetHeader>
            <div className="mt-4 grid grid-cols-2 gap-2 overflow-y-auto">
              {grouped.map(({ cat, totalCount }) => (
                <button
                  key={cat.id}
                  onClick={() => scrollToCat(cat.id)}
                  className="flex items-center justify-between gap-2 px-4 py-3 rounded-lg border border-border bg-white text-left hover:border-brand-orange transition"
                  data-testid={`jump-to-${cat.id}`}
                >
                  <span className="font-heading font-semibold text-sm truncate">{cat.name}</span>
                  <span className="text-xs font-bold text-brand-orange shrink-0">{totalCount}</span>
                </button>
              ))}
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* Cart FAB + Bottom Sheet */}
      {cartCount > 0 && (
        <Sheet open={cartOpen} onOpenChange={setCartOpen}>
          <SheetTrigger asChild>
            <button
              className="fixed left-1/2 -translate-x-1/2 bottom-20 z-40 bg-brand-orange text-white rounded-full shadow-xl hover:bg-[#E65C00] flex items-center gap-3 pl-4 pr-5 py-3"
              data-testid="cart-fab"
            >
              <span className="relative">
                <ShoppingBag className="w-5 h-5" />
                <span className="absolute -top-1.5 -right-1.5 bg-white text-brand-orange text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center" data-testid="cart-count">{cartCount}</span>
              </span>
              <span className="font-bold text-sm">View order · {sym}{cartTotal.toFixed(2)}</span>
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-2xl max-h-[85vh] flex flex-col" data-testid="cart-sheet">
            <SheetHeader className="text-left">
              <div className="flex items-center justify-between">
                <div>
                  <SheetTitle className="font-heading">Your order</SheetTitle>
                  <SheetDescription>Show this summary at the counter to place your order.</SheetDescription>
                </div>
                <button onClick={clearCart} className="inline-flex items-center gap-1 text-xs font-bold text-destructive hover:opacity-80" data-testid="cart-clear">
                  <Trash2 className="w-3.5 h-3.5" /> Clear
                </button>
              </div>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto mt-4 -mx-6 px-6 space-y-3">
              {cartLines.map(({ item, qty }) => (
                <div key={item.id} className="flex items-center gap-3 py-2 border-b border-border last:border-0" data-testid={`cart-line-${item.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm break-words">{item.name}</div>
                    <div className="text-xs text-muted-foreground">{sym}{Number(item.price).toFixed(2)} each</div>
                  </div>
                  <div className="inline-flex items-center gap-1.5 rounded-full border border-border px-1.5 py-1">
                    <button onClick={() => decItem(item)} className="w-6 h-6 rounded-full bg-muted flex items-center justify-center" aria-label="Remove one">
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="font-bold text-sm min-w-[18px] text-center">{qty}</span>
                    <button onClick={() => addItem(item)} className="w-6 h-6 rounded-full bg-brand-orange text-white flex items-center justify-center" aria-label="Add one">
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="font-heading font-bold brand-orange whitespace-nowrap min-w-[72px] text-right">{sym}{(qty * Number(item.price)).toFixed(2)}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 border-t border-border pt-4 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Total</div>
                <div className="font-heading font-extrabold text-2xl brand-orange" data-testid="cart-total">{sym}{cartTotal.toFixed(2)}</div>
                <div className="text-[10px] text-muted-foreground">{cartCount} item{cartCount !== 1 ? "s" : ""} · taxes may apply</div>
              </div>
              <Button onClick={() => setCartOpen(false)} className="bg-brand-navy hover:bg-[#1A2B42] text-white rounded-full h-11 px-6" data-testid="cart-close">
                <X className="w-4 h-4" /> Done
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* Footer — Powered by MenuMojo */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-brand-navy border-t border-white/10">
        <a href="/" className="max-w-md mx-auto px-5 py-3.5 flex items-center justify-center gap-2 text-white" data-testid="powered-by-footer">
          <img src={LOGO_URL} alt="" className="w-6 h-6 object-contain" />
          <span className="text-sm font-semibold">Powered by <span className="brand-orange">MenuMojo</span></span>
          <ExternalLink className="w-3.5 h-3.5 text-white/50" />
        </a>
      </div>
    </div>
  );
}
